import { apiClient, ApiResponse } from './api-client'
import type { Business, CreateBusinessData, UpdateBusinessData } from '../models/business'

export interface BusinessFilters {
  status?: 'all' | 'active' | 'inactive'
}

export class BusinessService {
  /**
   * Get all businesses for the authenticated user
   */
  async getBusinesses(filters?: BusinessFilters): Promise<Business[]> {
    const params = new URLSearchParams()
    
    if (filters?.status && filters.status !== 'all') {
      params.append('status', filters.status)
    }

    const queryString = params.toString()
    const url = `/businesses${queryString ? `?${queryString}` : ''}`
    
    const response = await apiClient.get<ApiResponse<{ businesses: Business[] }>>(url)
    if (response.data.success && response.data.data) {
      return response.data.data.businesses || []
    }
    throw new Error(response.data.error?.message || 'Failed to get businesses')
  }

  /**
   * Create new business
   */
  async createBusiness(data: CreateBusinessData): Promise<Business> {
    const response = await apiClient.post<ApiResponse<{ business: Business }>>('/businesses', data)
    if (response.data.success && response.data.data) {
      return response.data.data.business
    }
    throw new Error(response.data.error?.message || 'Failed to create business')
  }

  /**
   * Update business
   */
  async updateBusiness(id: string, data: UpdateBusinessData): Promise<Business> {
    const response = await apiClient.put<ApiResponse<{ business: Business }>>(`/businesses/${id}`, data)
    if (response.data.success && response.data.data) {
      return response.data.data.business
    }
    throw new Error(response.data.error?.message || 'Failed to update business')
  }

  /**
   * Upload business logo
   */
  async uploadLogo(id: string, file: File): Promise<string> {
    const formData = new FormData()
    formData.append('logo', file)
    const response = await apiClient.post<ApiResponse<{ logoUrl: string }>>(
      `/businesses/${id}/logo`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    )
    if (response.data.success && response.data.data) {
      return response.data.data.logoUrl
    }
    throw new Error(response.data.error?.message || 'Failed to upload logo')
  }

  /**
   * Get business by ID
   * Works for all authenticated users:
   * - BUSINESS_OWNER: can access any business they own
   * - EMPLOYEE/CASHIER: can only access their own business (from token)
   * - SYSTEM_ADMIN: can access any business
   */
  async getBusinessById(id: number): Promise<Business> {
    const response = await apiClient.get<ApiResponse<{ business: Business }>>(`/businesses/${id}`)
    if (response.data.success && response.data.data) {
      return response.data.data.business
    }
    throw new Error(response.data.error?.message || 'Business not found')
  }

  /**
   * Get business by QR code
   */
  async getBusinessByQrCode(qrCode: string): Promise<Business> {
    const response = await apiClient.get<ApiResponse<{ business: Business }>>(`/businesses/qr/${qrCode}`)
    if (response.data.success && response.data.data) {
      return response.data.data.business
    }
    throw new Error(response.data.error?.message || 'Business not found')
  }

  /**
   * Delete business (soft delete)
   */
  async deleteBusiness(id: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse>(`/businesses/${id}`)
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to delete business')
    }
  }

  /**
   * Get dashboard statistics
   */
  async getDashboardStats(): Promise<{
    totalBusinesses: number
    todayOrders: number
    totalMenuItems: number
    totalRevenue: number
  }> {
    const response = await apiClient.get<ApiResponse<{
      totalBusinesses: number
      todayOrders: number
      totalMenuItems: number
      totalRevenue: number
    }>>('/businesses/dashboard/stats')
    if (response.data.success && response.data.data) {
      return response.data.data
    }
    throw new Error(response.data.error?.message || 'Failed to get dashboard stats')
  }
}

// Export singleton instance
export const businessService = new BusinessService()

